import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import PasswordInput from "../../components/PasswordInput";
import { useAdminAuth } from "../../context/AdminAuthContext";

export default function AdminChangePassword() {
  const { admin, changePassword } = useAdminAuth();
  const navigate = useNavigate();

  const mustChange = !admin?.passwordChangedAt;

  const [form, setForm] = useState({
    current: "",
    next: "",
    confirm: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    if (form.next.length < 8) {
      setError("New password must be at least 8 characters");
      return;
    }
    if (form.next !== form.confirm) {
      setError("New passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await changePassword(form.current, form.next);
      setSuccess(true);
      setForm({ current: "", next: "", confirm: "" });
      if (mustChange) {
        setTimeout(() => navigate("/admin/users", { replace: true }), 1000);
      }
    } catch (err) {
      setError(err.message || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Change admin password
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Admin password changes take effect immediately.
        </p>
      </div>

      {mustChange && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          <KeyRound className="mt-0.5 h-4 w-4 flex-none" />
          <span>
            You are using the default bootstrap password. Please change it
            before doing anything else.
          </span>
        </div>
      )}

      <form onSubmit={onSubmit} className="card space-y-4 p-6">
        <div>
          <label className="label-base">Current password</label>
          <PasswordInput
            value={form.current}
            onChange={update("current")}
            autoComplete="current-password"
            placeholder="••••••••"
          />
        </div>
        <div>
          <label className="label-base">New password</label>
          <PasswordInput
            value={form.next}
            onChange={update("next")}
            autoComplete="new-password"
            placeholder="At least 8 characters"
          />
        </div>
        <div>
          <label className="label-base">Confirm new password</label>
          <PasswordInput
            value={form.confirm}
            onChange={update("confirm")}
            autoComplete="new-password"
            placeholder="Re-enter new password"
          />
        </div>
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        {success && (
          <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
            <CheckCircle2 className="h-4 w-4" />
            Password updated.
          </div>
        )}
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Change password
        </button>
      </form>
    </div>
  );
}
